"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { House as Home, Hammer, MapPin, FolderGit2, ArrowRight, ShieldCheck, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
interface ServiceItem {
  id: string;
  title: string;
  category: string;
  description: string;
  path: string;
  linkText: string;
  iconName: string;
  photoName?: string;
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  imageAlt: string;
}
const servicesList: ServiceItem[] = [{
  id: "residential",
  title: "Residential Scaffolding",
  category: "Domestic Access",
  description: "Safe, tidy scaffolding for extensions, loft conversions, painting, and domestic refurbishments with property protection.",
  path: "/ResidentialScaffoldingPage",
  linkText: "View residential services",
  iconName: "home",
  photoName: "Chimney Related Residential Image",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/residential_scaffolding_hero.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/residential_scaffolding_hero.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/residential_scaffolding_hero.png-wvc-sizes",
  imageAlt: "Residential scaffolding installed on a two-storey brick home"
}, {
  id: "roofing",
  title: "Roofing Scaffolding",
  category: "Specialist Trade",
  description: "Purpose-built edge protection, chimney stacks, and roof-level working platforms designed specifically for roofing contractors.",
  path: "/RoofingScaffoldingPage",
  linkText: "View roofing solutions",
  iconName: "hammer",
  photoName: "Chimney Related Roofing Image",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/roofing_scaffold_project.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/roofing_scaffold_project.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/roofing_scaffold_project.png-wvc-sizes",
  imageAlt: "Roofing edge protection and access tower on residential roofline"
}, {
  id: "areas",
  title: "Areas We Cover",
  category: "Regional Coverage",
  description: "Operating throughout Berkshire, Surrey, Hampshire, Buckinghamshire, London, and surrounding Home Counties.",
  path: "/AreasWeCoverPage",
  linkText: "Check local coverage",
  iconName: "map-pin",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/south_east_service_coverage.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/south_east_service_coverage.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/south_east_service_coverage.png-wvc-sizes",
  imageAlt: "Map view of South East coverage across Home Counties"
}, {
  id: "projects",
  title: "Projects & Case Studies",
  category: "Portfolio",
  description: "Explore completed scaffolding projects across residential, commercial, and solar installations demonstrating safety standards.",
  path: "/ProjectsCaseStudiesPage",
  linkText: "Browse project gallery",
  iconName: "folder",
  photoName: "Chimney Related Projects Image",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/scaffolding_portfolio_showcase.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/scaffolding_portfolio_showcase.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/scaffolding_portfolio_showcase.png-wvc-sizes",
  imageAlt: "BMF Scaffolding structure on high-specification domestic property"
}];
const renderIcon = (name: string) => {
  switch (name) {
    case "home":
      return <Home className="size-5 text-primary" />;
    case "hammer":
      return <Hammer className="size-5 text-primary" />;
    case "map-pin":
      return <MapPin className="size-5 text-primary" />;
    case "folder":
      return <FolderGit2 className="size-5 text-primary" />;
    default:
      return <ShieldCheck className="size-5 text-primary" />;
  }
};
function ServicesGridConsumer() {
  const {
    posts,
    loading
  } = usePosts();
  const shouldReduceMotion = useReducedMotion();
  const getServiceImage = (service: ServiceItem) => {
    if (!service.photoName) {
      return {
        src: service.imageSrc,
        srcset: service.imageSrcset,
        sizes: service.imageSizes,
        alt: service.imageAlt
      };
    }
    const matchedPost = posts.find(p => p.customFields?.photo_name?.trim().toLowerCase() === service.photoName?.trim().toLowerCase() || p.title?.trim().toLowerCase() === service.photoName?.trim().toLowerCase());
    const cptImage = matchedPost?.customFields?.image || matchedPost?.featuredImage;
    if (cptImage) {
      return {
        src: cptImage,
        srcset: "",
        sizes: "",
        alt: matchedPost?.customFields?.alt_text || matchedPost?.title || service.imageAlt
      };
    }
    return {
      src: service.imageSrc,
      srcset: service.imageSrcset,
      sizes: service.imageSizes,
      alt: service.imageAlt
    };
  };
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
      {servicesList.map((service, i) => {
      const image = getServiceImage(service);
      return <div key={service.id} data-index={i} className="flex">
            <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.15
        }} transition={{
          duration: 0.45,
          delay: i * 0.08,
          ease: "easeOut"
        }} className="w-full flex">
              <Card className="flex flex-col justify-between w-full border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300 rounded-xl overflow-hidden hover:border-primary/40 group">
                <div className="relative aspect-16/10 overflow-hidden bg-muted">
                  {loading && service.photoName ? <div className="h-full w-full bg-muted animate-pulse" /> : <img src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={image.alt} loading="lazy" decoding="async" className="h-full w-full object-cover group-hover:scale-105 transition-[transform] duration-500" />}
                  <div className="absolute top-3 left-3">
                    <span className="inline-flex items-center gap-1.5 rounded-md bg-secondary/90 px-2.5 py-1 text-xs font-medium text-secondary-foreground backdrop-blur-xs">
                      {service.category}
                    </span>
                  </div>
                </div>

                <CardHeader className="pb-3 pt-5">
                  <div className="flex items-center gap-2.5 mb-2">
                    <div className="p-2 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      {renderIcon(service.iconName)}
                    </div>
                    <CardTitle className="text-xl font-bold tracking-tight text-foreground font-default leading-snug">
                      {service.title}
                    </CardTitle>
                  </div>
                  <CardDescription className="text-sm text-muted-foreground line-clamp-3 leading-relaxed">
                    {service.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="pt-0 flex-1" />

                <CardFooter className="pt-3 pb-6 border-t border-border/60">
                  <Button asChild variant="ghost" size="sm" className="w-full justify-between px-2 text-primary hover:text-primary hover:bg-primary/5 group/btn font-medium">
                    <Link to={service.path}>
                      <span>{service.linkText}</span>
                      <ArrowRight className="size-4 ml-2 transition-[transform] duration-200 group-hover/btn:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>
          </div>;
    })}
    </div>;
}
const wpQueryParams = {
  post_type: "site_photo",
  per_page: 50
};
const sitePhotosQuery = new WP_Query(wpQueryParams);
export default function RelatedServices() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="relatedservices" data-nav="light" className="relative bg-background py-20 md:py-28 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-14 md:mb-20">
          <Badge variant="outline" className="mb-4 py-1 px-3 border-primary/30 text-primary bg-primary/5 font-medium tracking-wide">
            Connected Solutions
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
            Explore related scaffolding services
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Find access solutions tailored for domestic renovations, roofing contractors, and regional projects across the Home Counties.
          </p>
        </div>

        {/* Services Grid with PostsProvider */}
        <PostsProvider wp_query={{
        post_type: "site_photo",
        per_page: 50,
        status: ["publish"]
      }}>
          <ServicesGridConsumer />
        </PostsProvider>

        {/* Bottom CTA Banner */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        delay: 0.2,
        ease: "easeOut"
      }} className="mt-14 md:mt-20 rounded-2xl bg-secondary text-secondary-foreground p-8 sm:p-10 lg:p-12 shadow-lg border border-border/40 relative overflow-hidden">
          <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8 text-center lg:text-left">
            <div className="max-w-2xl">
              <div className="flex items-center justify-center lg:justify-start gap-2 mb-3">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary-foreground/90 bg-primary px-3 py-1 rounded-full">
                  <ShieldCheck className="size-3.5" />
                  NASC & CHAS
                </span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-secondary-foreground font-default">
                Ready to plan your domestic or roofing project?
              </h3>
              <p className="mt-2 text-secondary-foreground/80 text-sm sm:text-base leading-relaxed">
                Contact Pete Wood and the operational team for straightforward pricing, rapid site surveys, and dedicated safety planning.
              </p>
              <div className="mt-4 flex flex-wrap items-center justify-center lg:justify-start gap-4 text-xs sm:text-sm text-secondary-foreground/75">
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  20+ years trade experience
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  Fixed-scope quotes
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  South East coverage
                </span>
              </div>
            </div>

            <div className="shrink-0">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-8 py-6 rounded-md shadow-md text-base">
                <Link to="/ContactRequestQuotePage">
                  Request a Residential Quote
                  <ArrowRight className="size-5 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}